import type { Aggregates } from 'hooks/useSpaceSearch';
import type { ComponentProps } from 'react';
import { FC, ReactNode, useContext, useMemo, useState } from 'react';
import { dateFormatForSearchUI } from 'utils/date';
import moment from 'utils/dayjs';
import { useTranslation } from 'utils/i18n';
import { CCG_BOOKING_RESTRICTION } from '../../constants/bookings';
// constants
import { distances } from '../../constants/distances';
import { SearchContext } from '../../state/search';
import CheckBoxList from '../fields/checkBoxList';
import DatepickerField from '../fields/datepickerField';
import FilterDropdown from '../filterDropdown';
import IncrementalInput from '../incrementalInput';
import Switch from '../switch';

type View = 'list' | 'calendar';

interface Props {
  changeViewType?: (view: View) => void;
  updateCurrentPage?: (pageNumber: number) => void;
  currentViewType?: View;
  aggregates?: Aggregates;
  showPropertyFilter?: boolean;
  children?: ReactNode;
}

const defaultAggregates: Aggregates = { property: [], distance: [] };

// DatepickerField forwards extra props (maxDate, isClearable) through to the
// underlying react-datepicker but does not declare them on its Props type.
const DatepickerFieldWithExtras = DatepickerField as unknown as FC<
  ComponentProps<typeof DatepickerField> & {
    maxDate?: Date;
    isClearable?: boolean;
  }
>;

const toggle = (value: string) => (previous: string[]) =>
  previous.includes(value)
    ? previous.filter((item: string) => item !== value)
    : [...previous, value];

const distanceLabel = (value: number | null) =>
  Object.keys(distances).find(
    key => distances[key as keyof typeof distances] === value
  ) ?? 'Distance';

const SpaceFilters: FC<Props> = ({
  changeViewType,
  currentViewType = 'list',
  updateCurrentPage = () => {},
  aggregates = defaultAggregates,
  showPropertyFilter = true,
  children,
}) => {
  const { t } = useTranslation('common');
  const { filters, actions } = useContext(SearchContext);

  const [distance, setDistance] = useState(filters.distance);
  const [capacity, setCapacity] = useState(filters.capacity);
  const [properties, setProperty] = useState(filters.property);
  const [facets, setFacets] = useState(filters.facets);

  const showDistanceFilter = filters.location || filters.latLng;

  const maxDate = useMemo(
    () => moment().add(CCG_BOOKING_RESTRICTION, 'months').toDate(),
    []
  );

  const clearSelectedCheckboxes = () => {
    setDistance(null);
    setCapacity(null);
    setProperty([]);
    setFacets([]);
  };

  return (
    <div>
      {changeViewType && (
        <div className="space-filters__switch">
          <Switch
            label="Calendar View"
            toggled={currentViewType === 'calendar'}
            onSwitchChange={
              changeViewType as unknown as (toggled: boolean) => void
            }
          />
        </div>
      )}
      <div
        className="space-filters"
        data-cy="spaceFilters"
        data-testid="spaceFilters"
      >
        <div className="space-filters__container">
          <DatepickerFieldWithExtras
            value={filters.date}
            onChange={(date: Date | null) => {
              updateCurrentPage(1);
              actions.updateDate(date);
            }}
            placeholder="Date"
            dateFormat={dateFormatForSearchUI}
            dataCy="spaceFiltersDatePicker"
            maxDate={maxDate}
            isClearable
          />
          {showPropertyFilter && (
            <FilterDropdown
              label="Property"
              hideSelectAllButton
              handleApply={() => {
                updateCurrentPage(1);
                actions.updateProperty(properties);
              }}
              handleClearAll={() => {
                setProperty([]);
                updateCurrentPage(1);
                actions.updateProperty([]);
              }}
              selectedItems={filters.property}
            >
              <CheckBoxList
                id="property"
                name="property[]"
                options={(aggregates.property ?? []).map(
                  (i: { name: string; count: number }) => {
                    return {
                      name: `${i.name} (${i.count})`,
                      value: i.name,
                      checked: properties.includes(i.name),
                    };
                  }
                )}
                onChange={({ currentTarget: { value } }) => {
                  updateCurrentPage(1);
                  setProperty(toggle(value));
                }}
              />
            </FilterDropdown>
          )}

          {showDistanceFilter && (
            <FilterDropdown
              hideSelectAllButton
              label={distanceLabel(filters.distance)}
              handleApply={() => {
                updateCurrentPage(1);
                actions.updateDistance(distance);
              }}
              handleClearAll={() => {
                setDistance(null);
                updateCurrentPage(1);
                actions.updateDistance(null);
              }}
            >
              <CheckBoxList
                id="distance"
                name="distance"
                type="radio"
                className="field--radio-list"
                options={Object.keys(distances).map(key => ({
                  name: key,
                  value: distances[key as keyof typeof distances],
                  checked:
                    distances[key as keyof typeof distances] === distance,
                }))}
                onChange={({ target: { value } }) => setDistance(Number(value))}
              />
            </FilterDropdown>
          )}

          <FilterDropdown
            hideSelectAllButton
            label={
              filters.capacity === null
                ? 'Capacity'
                : `Guests: ${filters.capacity}`
            }
            clearAllText={t('reset')}
            canApply={!!capacity && capacity >= 1}
            handleApply={() => {
              setCapacity(capacity);
              updateCurrentPage(1);
              actions.updateCapacity(capacity);
            }}
            handleClearAll={() => {
              setCapacity(null);
              updateCurrentPage(1);
              actions.updateCapacity(null);
            }}
          >
            <IncrementalInput
              id="capacity"
              label="Guests:"
              initialValue={filters.capacity === null ? 0 : filters.capacity}
              minValue={1}
              onChange={setCapacity}
            />
          </FilterDropdown>

          {aggregates.facets && (
            <FilterDropdown
              hideSelectAllButton
              label="Features"
              handleApply={() => {
                setFacets(facets);
                updateCurrentPage(1);
                actions.updateFacets(facets);
              }}
              handleClearAll={() => {
                setFacets([]);
                updateCurrentPage(1);
                actions.updateFacets([]);
              }}
              selectedItems={filters.facets}
            >
              <CheckBoxList
                id="facet"
                name="facets[]"
                options={aggregates.facets.map(
                  (i: { name: string; count: number }) => {
                    return {
                      name: `${i.name} (${i.count})`,
                      value: i.name,
                      checked: facets.includes(i.name),
                    };
                  }
                )}
                onChange={({ currentTarget: { value } }) =>
                  setFacets(toggle(value))
                }
              />
            </FilterDropdown>
          )}
        </div>
        {children}
        <button
          className="button button--text"
          onClick={() => {
            updateCurrentPage(1);
            actions.clearAllFilters();
            clearSelectedCheckboxes();
          }}
        >
          {t('clear_all')}
        </button>
      </div>
    </div>
  );
};

export default SpaceFilters;
